import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../services/supabase'
import { Plus, List, Trello, BarChart2, Search, X, Trash2 } from 'lucide-react'
import { TaskBin } from '../../components/Tasks/TaskBin'
import { TaskList } from '../../components/Tasks/TaskList'
import { TaskBoard } from '../../components/Tasks/TaskBoard'
import { TaskDashboard } from '../../components/Tasks/TaskDashboard'
import { TaskDetailPanel } from './TaskDetailPanel'
import { CreateTaskModal } from '../../components/Tasks/CreateTaskModal'
import { useAdvisors } from '../../hooks/useAdvisors'
import { useCurrentAdvisor } from '../../hooks/useCurrentAdvisor'

const SECTIONS = [
  'All Sections', 'Claims',
]

const STATUSES = [
  { value: 'all',         label: 'All Status' },
  { value: 'open',        label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed' },
  { value: 'cancelled',   label: 'Cancelled' },
]

const VIEWS = [
  { key: 'list',      label: 'List',      icon: List },
  { key: 'board',     label: 'Board',     icon: Trello },
  { key: 'dashboard', label: 'Dashboard', icon: BarChart2 },
  { key: 'bin',       label: 'Bin',       icon: Trash2 },
]

// Only tasks pushed from the Dispatch portal (claim_id is set) belong here
const isClaimsTask = (t) => t.claim_id != null

export function ClaimsTasksPage({ isDarkMode, initialTaskId }) {
  const { advisors } = useAdvisors()
  const { advisor: currentAdvisor } = useCurrentAdvisor()

  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)
  const [activeView, setActiveView] = useState('list')
  const [selectedTaskId, setSelectedTaskId] = useState(initialTaskId ?? null)
  const [createOpen, setCreateOpen] = useState(false)

  // Filters - auto-set to current advisor
  const [filterSection, setFilterSection] = useState('All Sections')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterAdvisor, setFilterAdvisor] = useState(() => currentAdvisor?.id ?? 'all')
  const [search, setSearch] = useState('')

  // Update filterAdvisor when currentAdvisor loads
  // Owners see all tasks; regular advisors see only their tasks
  useEffect(() => {
    if (initialTaskId) setSelectedTaskId(initialTaskId)
  }, [initialTaskId])

  useEffect(() => {
    if (currentAdvisor?.id) {
      setFilterAdvisor(currentAdvisor.is_owner ? 'all' : currentAdvisor.id)
    }
  }, [currentAdvisor?.id, currentAdvisor?.is_owner])

  const fetchTasks = useCallback(async () => {
    const { data, error } = await supabase
      .from('sfp_tasks')
      .select(`
        *,
        advisors!advisor_id(id, full_name, avatar_url),
        sfp_task_advisors(advisor_id, advisors!advisor_id(id, full_name, avatar_url))
      `)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(500)

    if (error) {
      console.error('fetchTasks (claims) error:', error)
      setFetchError(error.message)
    } else {
      setFetchError(null)
      setTasks((data ?? []).filter(isClaimsTask))
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchTasks()
    const channel = supabase
      .channel('claims-page-all')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sfp_tasks' }, fetchTasks)
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [fetchTasks])

  const filtered = tasks.filter((t) => {
    if (filterSection !== 'All Sections' && (t.section ?? 'Claims') !== filterSection) return false
    if (filterStatus !== 'all' && t.status !== filterStatus) return false

    // Filter by advisor: show if current advisor is primary OR co-advisor
    if (filterAdvisor !== 'all') {
      const isPrimaryAdvisor = t.advisor_id === filterAdvisor
      const isCoAdvisor = (t.sfp_task_advisors || []).some(ta => ta.advisor_id === filterAdvisor)
      if (!isPrimaryAdvisor && !isCoAdvisor) return false
    }

    if (search) {
      const q = search.toLowerCase()
      const hay = [t.title, t.claim_number, t.client_name, t.policy_number].join(' ').toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })

  const handleTaskDeleted = (deletedId) => {
    setTasks(prev => prev.filter(t => t.id !== deletedId))
    setSelectedTaskId(null)
  }

  const showPanel = selectedTaskId && activeView !== 'bin'

  const bg = isDarkMode ? 'bg-slate-900' : 'bg-gray-50'
  const border = isDarkMode ? 'border-slate-800' : 'border-gray-200'
  const inputCls = `px-3 py-1.5 text-sm rounded-lg border outline-none focus:ring-2 focus:ring-sfp-orange/30 transition ${
    isDarkMode
      ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500'
      : 'bg-white border-gray-200 text-gray-700 placeholder-gray-400'
  }`

  return (
    <div className={`flex flex-col h-full ${bg}`}>
      {/* ── Top toolbar ── */}
      <div className={`flex items-center gap-2 px-4 py-2.5 border-b flex-wrap flex-shrink-0 ${border} ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className={`flex rounded-lg border overflow-hidden ${border}`}>
          {VIEWS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveView(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                activeView === key
                  ? 'bg-sfp-orange text-white'
                  : isDarkMode
                    ? 'text-slate-400 hover:bg-slate-700'
                    : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-sfp-orange text-white hover:bg-sfp-orange/90 transition-colors flex-shrink-0"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add Task</span>
        </button>
      </div>

      {/* ── Filter bar ── */}
      <div className={`flex items-center gap-2 px-4 py-2 border-b flex-wrap flex-shrink-0 ${border} ${isDarkMode ? 'bg-slate-900/80' : 'bg-white'}`}>
        <div className="relative">
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search claims..."
            className={`${inputCls} pl-8 w-44`}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <X className={`w-3 h-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
            </button>
          )}
        </div>

        <select value={filterSection} onChange={(e) => setFilterSection(e.target.value)} className={inputCls}>
          {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={inputCls}>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        <select value={filterAdvisor} onChange={(e) => setFilterAdvisor(e.target.value)} className={inputCls}>
          <option value="all">All Advisers</option>
          {advisors.map((a) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
        </select>

        <span className={`text-xs ml-auto flex-shrink-0 flex items-center gap-1.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
            fetchError ? 'bg-red-500' : loading ? 'bg-yellow-400 animate-pulse' : 'bg-green-500'
          }`} />
          {loading ? 'Loading...' : !fetchError && `${filtered.length} claim${filtered.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* ── Main content + detail panel ── */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-col overflow-hidden flex-1">
          {fetchError ? (
            <div className="m-4 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <p className="font-semibold mb-1">Could not load claims</p>
              <p className="font-mono text-xs">{fetchError}</p>
            </div>
          ) : loading ? (
            <div className={`flex items-center justify-center h-48 text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Loading claims...
            </div>
          ) : (
            <>
              {activeView === 'list' && (
                <TaskList
                  tasks={filtered}
                  selectedTaskId={selectedTaskId}
                  onSelectTask={setSelectedTaskId}
                  isDarkMode={isDarkMode}
                />
              )}
              {activeView === 'board' && (
                <TaskBoard
                  tasks={filtered}
                  onSelectTask={setSelectedTaskId}
                  isDarkMode={isDarkMode}
                />
              )}
              {activeView === 'dashboard' && (
                <TaskDashboard
                  tasks={filtered}
                  isDarkMode={isDarkMode}
                  onSelectTask={setSelectedTaskId}
                />
              )}
              {activeView === 'bin' && (
                <TaskBin
                  isDarkMode={isDarkMode}
                  onRestored={fetchTasks}
                />
              )}
            </>
          )}
        </div>

        {showPanel && (
          <div className={`w-[520px] xl:w-[620px] flex-shrink-0 border-l overflow-hidden ${border} ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
            <TaskDetailPanel
              taskId={selectedTaskId}
              isDarkMode={isDarkMode}
              onClose={() => setSelectedTaskId(null)}
              onTaskUpdated={fetchTasks}
              onTaskDeleted={handleTaskDeleted}
            />
          </div>
        )}
      </div>

      <CreateTaskModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={fetchTasks}
        isDarkMode={isDarkMode}
      />
    </div>
  )
}
